import { Checkbox } from 'antd';
import { Interweave } from 'interweave';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Icon, InfoTooltip } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { severities } from 'utils/app-configs';
import { newSearchTypes } from '../../../../utils/app-configs';
import { resetAllAdditionalFilterParams } from '../../../../worklist/containers/worklist-actions';
import { Action } from '../../value-filter';
import {
  CheckBoxContainer,
  ClearIcon,
  EntitySelect,
  Footer,
  FooterText,
  HighlightSpan,
  InfoContainer,
  ItemTitle,
  Remove,
  SelectOption,
  ShowChildren,
  SingleLineSpan,
  StyledCheckbox,
  StyledIcon,
  StyledIconText,
  SupportingIcon,
  Title,
  TypesDropdown,
} from '../styles';

const EntityFilter = ({
  options = [],
  performSearch,
  performSearchAll,
  onChange,
  id,
  totalCount,
  activeOptions = [],
  description,
  intl,
  resetOptions,
  multiType = false,
  types = [],
  isColumnFilter,
  needsConfirm,
  filters,
  sort,
  worklistId,
  fixOptions = [],
  info = '',
  isMultiEntity,
  operators = [],
  showChildren = false,
  shouldResetAllAdditionalFilterParams,
  currentFilterValue = [],
  dropdownMatchSelectWidth = false,
}) => {
  const dispatch = useDispatch();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [entities, setEntities] = useState([]);
  const [searchValue, setSearchValue] = useState();
  const [shouldReload, setShouldReload] = useState(false);
  const [selectedType, setSelectedType] = useState();
  const [showingChildren, setShowingChildren] = useState(showChildren);
  const [inputKey, setInputKey] = useState(0);
  const isLoading = useSelector(state => state.worklist.isDataLoading);

  const totalDataCount = totalCount > 100 ? 100 : totalCount;

  useDeepCompareEffect(() => {
    if (multiType && types?.length) {
      setSelectedType(
        isMultiEntity && activeOptions?.length ?
          types?.find(t => t.id.toLowerCase() === activeOptions[0].type?.toLowerCase())?.id :
          types?.find(t => t.isDefault)?.id ||
          types[0]?.id,

      );
    }
  }, [types]);

  useEffect(() => {
    if (!showChildren && showingChildren && fixOptions?.length) {
      setShowingChildren(showChildren);
    }
  }, [showChildren]);

  useDeepCompareEffect(() => {
    setEntities(options?.map((o) => {
      const selectedOption = selectedOptions?.find(so => so.id === o.id);
      if (selectedOption) {
        return {
          ...o,
          selected: true,
          isDisabled: selectedOption.isDisabled,
        };
      }
      return {
        ...o,
        selected: false,
        isDisabled: false,
      };
    }));
  }, [options, selectedOptions]);

  useDeepCompareEffect(() => {
    if (selectedOptions && shouldReload && !needsConfirm) {
      const operator = operators.find(o => o.id === 'showChildren');
      const valueString = selectedOptions?.map(o => o.id).join(';');
      if (selectedOptions?.length) {
        onChange(showingChildren && operator ?
          operator.shortHand.replace(/{value}/, valueString) :
          valueString,
        );
      } else {
        onChange('');
      }
    }
    if (selectedOptions && !shouldReload) {
      setShouldReload(true);
    }
  }, [selectedOptions, showingChildren]);

  const search = useCallback(
    debounce((v) => {
      if (v) {
        const type = multiType ? selectedType : id;
        performSearch({
          type,
          searchValue: v,
          filters,
          sort,
          worklistId,
          pageSize: 5,
          oldSearch: !newSearchTypes?.find(t => t === type?.toLowerCase()),
        });
      }
    }, 300),
    [performSearch, multiType, selectedType, id, filters],
  );

  useDeepCompareEffect(() => {
    if (!activeOptions?.length) {
      if (shouldResetAllAdditionalFilterParams) {
        setShowingChildren(false);
        dispatch(resetAllAdditionalFilterParams(false));
      }
      setShouldReload(true);
      setSelectedOptions(selectedOptions?.filter(o => o.isDisabled));
    }
    if (activeOptions?.length) {
      setShouldReload(false);
      const optionsToSet = activeOptions?.map((option) => {
        const fixed = fixOptions?.find(f => f.includes(option.id));
        if (fixed) {
          setShowingChildren(currentFilterValue?.find(c =>
            c.includes(option.id))?.startsWith('showChildren'),
          );
          return {
            ...option,
            line1: option.description,
            isDisabled: true,
            selected: true,
            hasLine2: option?.line2,
          };
        }
        return { ...option, line1: option.description, selected: true, hasLine2: option?.line2 };
      });
      setSelectedOptions(optionsToSet);
    }
  }, [activeOptions]);

  const searchAll = (v) => {
    const type = multiType ? selectedType : id;
    performSearchAll({
      type,
      searchValue: v,
      filters,
      sort,
      pageSize: totalDataCount,
      worklistId,
      oldSearch: !newSearchTypes?.find(t => t === type?.toLowerCase()),
    });
  };

  const handleSearch = (v) => {
    setSearchValue(v);
    search(v);
  };

  const resetInputSearchValue = () => {
    // eslint-disable-next-line no-undefined
    setSearchValue(undefined);
    setInputKey(prevKey => prevKey + 1);
  };

  const handleSelect = (v, forceRemove = false) => {
    if (v === 'footer') {
      const type = multiType ? selectedType : id;
      if (!newSearchTypes?.find(t => t === type?.toLowerCase())) {
        searchAll(searchValue);
      }
      return;
    }

    if (forceRemove) {
      setSelectedOptions(o => o?.filter(i => i.id !== v));
      return;
    }

    const entity = entities?.find(e => e.id === v);
    if (entity.selected) {
      setSelectedOptions(o => o?.filter(i => i.id !== v));
    } else {
      setSelectedOptions(o => o ?
        [...o, { ...entity, selected: true }] :
        [{ ...entity, selected: true }],
      );
    }
    setShouldReload(true);
    resetInputSearchValue();
  };

  const handleShowChildren = () => {
    setShowingChildren(!showingChildren);
  };

  const handleClear = () => {
    resetOptions();
    resetInputSearchValue();
  };

  const handleRemoveAll = () => {
    setSelectedOptions(selectedOptions?.filter(o => o.isDisabled));
    resetInputSearchValue();
    if (showingChildren) {
      setShowingChildren(false);
    }
  };

  const renderTitleLine = (text, icon) =>
    <SingleLineSpan>
      {icon ? <Icon type={icon} marginRight /> : null}
      <ItemTitle dangerouslySetInnerHTML={{ __html: text }} />
    </SingleLineSpan>;

  const renderShortLine = (text, icon, subtext) =>
    <SingleLineSpan>
      <ItemTitle dangerouslySetInnerHTML={{ __html: text }} />
      {' ('}<HighlightSpan dangerouslySetInnerHTML={{ __html: subtext }} />{')'}
    </SingleLineSpan>;

  const renderLine = (text, icon, isInactive = false) =>
    <SingleLineSpan>
      {icon ? <SupportingIcon type={icon} marginRight /> : null}
      <HighlightSpan dangerouslySetInnerHTML={{ __html: text }} />
      {isInactive &&
        <StyledIconText
          iconType='lock'
          severity={severities.INACTIVE}
          translationKey='search.INACTIVE'
        />
      }
    </SingleLineSpan>;

  const getPlaceHolder = () => {
    // eslint-disable-next-line no-undefined
    if (selectedType === undefined && multiType) {
      return null;
    }
    if (!multiType) {
      return intl.formatMessage({
        id: `worklist.filters.ENTITY_PLACEHOLDER_${id?.toUpperCase().replace('LINKED', '')}`,
      });
    }
    return intl.formatMessage({
      id:
        `worklist.filters.ENTITY_PLACEHOLDER_${selectedType?.toUpperCase()}`,
    });
  };

  const isNewSearch = newSearchTypes?.find(t =>
    t === (multiType ? selectedType : id)?.toLowerCase(),
  );

  return (
    <>
      <div>
        {!isColumnFilter && <Title>
          {description?.toUpperCase()}
          <InfoTooltip content={info} />
        </Title>}
        {selectedOptions?.filter(o => !o.isDisabled)?.length ?
          <Remove onClick={handleRemoveAll}>
            {`(${intl.formatMessage({ id: 'worklist.filters.ENTITY_REMOVE_ALL' })})`}
          </Remove> :
          null
        }
      </div>
      <Flex ffcn>
        {multiType ?
          <Flex onMouseDown={e => e.stopPropagation()}>
            <TypesDropdown
              values={types}
              valueKey='id'
              valueLabel='description'
              value={selectedType}
              onChange={setSelectedType}
              placement='bottomLeft'
              getPopupContainer={triggerNode => isColumnFilter ?
                document.body :
                triggerNode.parentNode
              }
              disabled={(isMultiEntity && selectedOptions?.length) || isLoading}
            />
            <ShowChildren
              checked={showingChildren}
              onChange={handleShowChildren}
              disabled={false}
            >
              <FormattedMessage id='worklist.filters.SHOW_CHILDREN' />
            </ShowChildren>
          </Flex> :
          null
        }
        <EntitySelect
          key={inputKey}
          value={searchValue}
          showSearch={true}
          autoClearSearchValue={false}
          placeholder={getPlaceHolder()}
          showArrow={false}
          onSearch={handleSearch}
          onSelect={v => handleSelect(v, false)}
          onBlur={handleClear}
          clearIcon={<ClearIcon type='times-circle' onClick={handleClear} />}
          allowClear={true}
          getPopupContainer={triggerNode => isColumnFilter ? document.body : triggerNode.parentNode}
          filterOption={false}
          notFoundContent={null}
          mode='multiple'
          isColumnFilter={isColumnFilter}
          popupClassName={isColumnFilter && 'ag-custom-component-popup'}
          isNewSearch={isNewSearch}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          // eslint-disable-next-line no-undefined
          disabled={isLoading || (multiType && selectedType === undefined)}
        >
          {isColumnFilter ?
            entities?.map(o =>
              <div key={o.id} disabled={o.isDisabled || isLoading}>
                <Flex ffrn aic>
                  <CheckBoxContainer marginRight={true}>
                    <Checkbox checked={o.selected} disabled={o.isDisabled || isLoading} />
                  </CheckBoxContainer>
                  <InfoContainer ffcn>
                    {renderShortLine(o.line1, o.line1Icon, o.line2)}
                  </InfoContainer>
                </Flex>
              </div>,
            ) :
            entities?.map(o =>
              <SelectOption key={o.id} selected={o.selected} disabled={o.isDisabled || isLoading}>
                <Flex ffrn>
                  <CheckBoxContainer>
                    <Checkbox checked={o.selected} disabled={o.isDisabled || isLoading} />
                  </CheckBoxContainer>
                  <InfoContainer ffcn>
                    {renderTitleLine(o.line1, o.line1Icon)}
                    {renderLine(o.line2, o.line2Icon, o.isInactive)}
                    {renderLine(o.line3, o.line3Icon, o.isInactive)}
                    {renderLine(o.line4, o.line4Icon, o.isInactive)}
                  </InfoContainer>
                </Flex>
              </SelectOption>,
            )}
          {(totalDataCount > entities.length) || (entities.length === 0 && searchValue) ?
            <Footer
              key='footer'
              className='select-footer'
            >
              <FooterText isNewSearch={isNewSearch}>
                {isNewSearch ?
                  <StyledIcon isColumnFilter={isColumnFilter}>
                    <Icon type='info-circle' size='lg' />
                  </StyledIcon> :
                  null
                }
                {totalDataCount > entities.length &&
                  <span>
                    {!isNewSearch ?
                      intl.formatMessage({ id: 'worklist.filters.SHOW_ALL_FILTERS' }) :
                      intl.formatMessage({ id: 'worklist.filters.ENTITY_REFINE_YOUR_SEARCH' })}
                  </span>
                }
                {entities.length === 0 && searchValue &&
                  <span>
                    {intl.formatMessage({ id: 'worklist.filters.NO_RESULTS_FOUND' })}
                  </span>
                }
              </FooterText>
            </Footer> :
            null}
        </EntitySelect>
        {selectedOptions?.sort((a, b) => b.isDisabled - a.isDisabled)?.map(o =>
          <div key={o.id}>
            <StyledCheckbox
              checked={o.selected}
              onChange={() => handleSelect(o.id, true)}
              disabled={o.isDisabled}
            >
              {/* eslint-disable-next-line */}
              <span><Interweave content={`${o.line1 ? o.line1 : o?.id?.split('|@|')?.pop()} ${o.hasLine2 ? `(${o?.line2})` : ''}`} /></span>
            </StyledCheckbox>
          </div>)}
      </Flex>
      {needsConfirm && <Action
        type='secondary'
        onClick={() => onChange(selectedOptions?.map(o => o.id))}
      >
        {intl.formatMessage({ id: 'worklist.filters.APPLY' })}
      </Action>
      }
    </>
  );
};

EntityFilter.propTypes = {
  options: PropTypes.array,
  performSearch: PropTypes.func,
  onChange: PropTypes.func,
  id: PropTypes.string,
  performSearchAll: PropTypes.func,
  totalCount: PropTypes.number,
  description: PropTypes.string,
  activeOptions: PropTypes.array,
  intl: PropTypes.object,
  resetOptions: PropTypes.func,
  multiType: PropTypes.bool,
  types: PropTypes.array,
  isColumnFilter: PropTypes.bool,
  needsConfirm: PropTypes.bool,
  filters: PropTypes.string,
  sort: PropTypes.string,
  worklistId: PropTypes.string,
  fixOptions: PropTypes.array,
  info: PropTypes.string,
  isMultiEntity: PropTypes.bool,
  operators: PropTypes.array,
  showChildren: PropTypes.bool,
  shouldResetAllAdditionalFilterParams: PropTypes.bool,
  currentFilterValue: PropTypes.array,
  dropdownMatchSelectWidth: PropTypes.bool,
};

export default injectIntl(EntityFilter);
